import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAdminSystem,
  getAssistantRuns,
  getIngestEvents,
  getAiUsage,
  runAssistant,
  type AdminSystem,
  type AssistantRun,
  type IngestEvent,
  type AiUsageSummary,
  type AiUsageCall,
} from '../api.js';
import {
  fmtBytes,
  fmtInsertedCounts,
  fmtDurationMs,
  currentMonth,
  shiftMonth,
  monthLabel,
  isAtCurrentMonth,
  isRecent,
} from '../lib/adminFormat.js';
import { fmtNumber } from '../lib/format.js';
import { relativeAgo } from '../lib/freshness.js';
import { Collapsible } from '../lifeos/ui.js';
import { Button, Group, Icon, IconTile, Page, Row } from '../ui/index.js';

/** Sum of the counted domain tables — the headline "rows tracked" number. */
function totalRows(dbRows: Record<string, number>): number {
  return Object.values(dbRows).reduce((a, n) => a + (Number.isFinite(n) ? n : 0), 0);
}

/** ISO → local "Jul 9, 2:14 PM"; "—" when null. */
function localDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** ISO → local time only, "2:14 PM". */
function localTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "$0.42" / "$0.0031" — always 2dp for the headline, 4dp for tiny per-call. */
function usd(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `$${n.toFixed(digits)}`;
}

// --- System section --------------------------------------------------------

function SystemSection(): JSX.Element {
  const [sys, setSys] = useState<AdminSystem | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getAdminSystem()
      .then((s) => alive && setSys(s))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : 'Failed to load system status.'));
    return () => {
      alive = false;
    };
  }, []);

  if (error) {
    return (
      <p className="bc-banner" role="alert">
        <Icon name="alert" size={18} />
        {error}
      </p>
    );
  }

  const v = (x: string): JSX.Element => <span className="num">{sys ? x : '…'}</span>;
  return (
    <Group header="System">
      <Row
        leading={<IconTile name="list" color="#0a84ff" />}
        title="Rows tracked"
        sub={sys ? `${Object.keys(sys.dbRows).length} tables` : undefined}
        detail={v(sys ? fmtNumber(totalRows(sys.dbRows), 0) : '')}
      />
      <Row leading={<IconTile name="archive" color="#5e5ce6" />} title="Database size" detail={v(sys?.dbSizePretty ?? '')} />
      <Row
        leading={<IconTile name="shield" color={sys && !sys.lastBackup ? '#ff3b30' : '#34c759'} />}
        title="Last backup"
        detail={sys && !sys.lastBackup ? <span className="bc-bad">None on record</span> : v(sys ? localDateTime(sys.lastBackup) : '')}
      />
      <Row leading={<IconTile name="activity" color="#ff9500" />} title="Last ingest" detail={v(sys ? localDateTime(sys.lastIngest) : '')} />
      <Row
        leading={<IconTile name="heart" color="#ff375f" />}
        title="Last Fitbit sync"
        detail={sys && !sys.lastFitbitSync ? <span className="bc-muted">Not connected</span> : v(sys ? localDateTime(sys.lastFitbitSync) : '')}
      />
    </Group>
  );
}

// --- Ingest feed -----------------------------------------------------------

const REFRESH_MS = 30_000;

function IngestSection(): JSX.Element {
  const [events, setEvents] = useState<IngestEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getIngestEvents({ limit: 50 })
      .then((e) => {
        setEvents(e);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load ingest feed.'));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const hasLive = useMemo(() => (events ?? []).some((e) => isRecent(e.receivedAt)), [events]);

  return (
    <section className="ui-groupwrap" aria-labelledby="admin-ingest">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="admin-ingest">
          Ingest feed
        </h2>
        <span className={`bc-live${hasLive ? ' bc-live--on' : ''}`}>
          {hasLive && <span className="bc-status bc-status--run" aria-hidden="true" />}
          {hasLive ? 'Live' : 'Every 30s'}
        </span>
      </div>
      {error && (
        <p className="bc-banner" role="alert">
          {error}
        </p>
      )}
      {events && events.length === 0 ? (
        <p className="bc-quiet">No ingest yet. Events appear after the first Apple Health push.</p>
      ) : (
        <ul className="ui-group">
          {events === null ? (
            <li className="ui-swipe">
              <div className="ui-row">
                <span className="skeleton bc-skel" />
              </div>
            </li>
          ) : (
            <Collapsible
              items={events}
              n={6}
              render={(e) => (
                <Row
                  key={e.id}
                  leading={<span className={`bc-status${isRecent(e.receivedAt) ? ' bc-status--run' : ' bc-status--good'}`} aria-hidden="true" />}
                  title={
                    <>
                      {e.source} <span className="bc-muted bc-detail-sm">· {fmtBytes(e.bytes)}</span>
                    </>
                  }
                  sub={`${fmtInsertedCounts(e.inserted)}${e.skippedCount ? ` · ${e.skippedCount} skipped` : ''} · ${fmtDurationMs(e.durationMs)}`}
                  detail={<span className="bc-detail-sm">{localTime(e.receivedAt)}</span>}
                />
              )}
            />
          )}
        </ul>
      )}
    </section>
  );
}

// --- AI usage --------------------------------------------------------------

/** Prettify a feature key: 'meal_parse' → 'Meal parse'. */
function featureLabel(feature: string): string {
  const s = feature.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function AiUsageSection(): JSX.Element {
  const [month, setMonth] = useState<string>(() => currentMonth());
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [calls, setCalls] = useState<AiUsageCall[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getAiUsage(month)
      .then((r) => {
        if (!alive) return;
        setSummary(r.summary);
        setCalls(r.calls);
        setError(null);
      })
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : 'Failed to load AI usage.'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [month]);

  const atCurrent = isAtCurrentMonth(month);
  const maxCost = useMemo(
    () => Math.max(0, ...(summary?.byFeature.map((f) => f.costUsd) ?? [0])),
    [summary],
  );

  return (
    <section className="ui-groupwrap" aria-labelledby="admin-ai">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="admin-ai">
          AI usage
        </h2>
        <div className="bc-month">
          <button type="button" className="bc-move" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Previous month">
            <Icon name="chevron-left" size={18} />
          </button>
          <span className="bc-month__label">{monthLabel(month)}</span>
          <button
            type="button"
            className="bc-move"
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
            disabled={atCurrent}
            aria-label="Next month"
          >
            <Icon name="chevron-right" size={18} />
          </button>
        </div>
      </div>

      {error && (
        <p className="bc-banner" role="alert">
          {error}
        </p>
      )}

      <div className="ui-card bc-usage">
        <div className="bc-usage__main">
          <span className="bc-usage__label">Estimated cost</span>
          <span className="bc-usage__value num">{summary ? usd(summary.costUsd) : '…'}</span>
        </div>
        <dl className="bc-usage__stats">
          <div>
            <dt>Calls</dt>
            <dd className="num">{summary ? fmtNumber(summary.calls, 0) : '…'}</dd>
          </div>
          <div>
            <dt>Tokens in</dt>
            <dd className="num">{summary ? fmtNumber(summary.inputTokens, 0) : '…'}</dd>
          </div>
          <div>
            <dt>Tokens out</dt>
            <dd className="num">{summary ? fmtNumber(summary.outputTokens, 0) : '…'}</dd>
          </div>
        </dl>
        {summary && summary.byFeature.length > 0 && (
          <ul className="bc-bars" aria-label="By feature">
            {summary.byFeature.map((f) => (
              <li className="bc-bar" key={f.feature}>
                <span className="bc-bar__label">{featureLabel(f.feature)}</span>
                <span className="bc-bar__value num">
                  {usd(f.costUsd, f.costUsd < 0.01 ? 4 : 2)}
                  <span className="bc-muted"> · {f.calls}</span>
                </span>
                <span className="bc-bar__track" aria-hidden="true">
                  <span
                    className="bc-bar__fill"
                    style={{ width: maxCost > 0 ? `${Math.max(2, (f.costUsd / maxCost) * 100)}%` : '2%' }}
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!loading && calls.length === 0 ? (
        <p className="ui-group__foot bc-foot">No AI calls recorded in {monthLabel(month)}.</p>
      ) : (
        <ul className="ui-group" aria-label="Calls">
          <Collapsible
            items={calls}
            n={6}
            noun="more calls"
            render={(c) => (
              <Row
                key={c.id}
                title={featureLabel(c.feature)}
                sub={`${c.model} · ${fmtNumber(c.inputTokens ?? 0, 0)} in / ${fmtNumber(c.outputTokens ?? 0, 0)} out · ${localTime(c.at)}`}
                detail={<span className="num">{usd(c.estCostUsd, (c.estCostUsd ?? 0) < 0.01 ? 4 : 2)}</span>}
              />
            )}
          />
        </ul>
      )}
    </section>
  );
}

// --- Assistant runs --------------------------------------------------------

/** Status → tone class for the run log; unknown statuses read neutral. */
function statusTone(status: string): string {
  if (status === 'done') return 'good';
  if (status === 'error') return 'bad';
  if (status === 'running') return 'run';
  return 'idle'; // skipped + anything else
}

/** Trim a long error to a single readable cell. */
function truncateError(error: string | null): string {
  if (!error) return '—';
  return error.length > 60 ? `${error.slice(0, 57)}…` : error;
}

function AssistantSection(): JSX.Element {
  const [runs, setRuns] = useState<AssistantRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kicking, setKicking] = useState(false);
  const [kickMsg, setKickMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    getAssistantRuns(50)
      .then((r) => {
        setRuns(r);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load assistant runs.'));
  }, []);

  useEffect(() => load(), [load]);

  function runWeekly(): void {
    if (kicking) return;
    setKicking(true);
    setKickMsg('Starting weekly run…');
    runAssistant({ trigger: 'weekly' })
      .then((r) => {
        setKickMsg(`Queued run ${r.runId.slice(0, 8)} — refreshing…`);
        setTimeout(() => {
          load();
          setKickMsg(null);
        }, 1500);
      })
      .catch((e: unknown) => setKickMsg(e instanceof Error ? e.message : 'Could not start run.'))
      .finally(() => setKicking(false));
  }

  return (
    <section className="ui-groupwrap" aria-labelledby="admin-runs">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="admin-runs">
          Assistant runs
        </h2>
        <Button variant="plain" size="sm" onClick={runWeekly} disabled={kicking}>
          {kicking ? 'Running…' : 'Run weekly now'}
        </Button>
      </div>
      {kickMsg && <p className="bc-note">{kickMsg}</p>}
      {error && (
        <p className="bc-banner" role="alert">
          {error}
        </p>
      )}
      {runs && runs.length === 0 ? (
        <p className="bc-quiet">No assistant runs yet. They appear after the first trigger fires.</p>
      ) : (
        <ul className="ui-group">
          {runs === null ? (
            <li className="ui-swipe">
              <div className="ui-row">
                <span className="skeleton bc-skel" />
              </div>
            </li>
          ) : (
            <Collapsible
              items={runs}
              n={6}
              render={(r) => (
                <Row
                  key={r.id}
                  leading={<span className={`bc-status bc-status--${statusTone(r.status)}`} aria-hidden="true" />}
                  title={
                    <>
                      {r.trigger}
                      {r.contextLabel && <span className="bc-muted"> · {r.contextLabel}</span>}
                    </>
                  }
                  sub={
                    <>
                      <span className={r.status === 'error' ? 'bc-bad' : undefined}>{r.status}</span> ·{' '}
                      {fmtNumber(r.inputTokens ?? 0, 0)} in / {fmtNumber(r.outputTokens ?? 0, 0)} out · {r.toolCalls ?? 0} tools
                      {r.error && (
                        <span className="bc-sub-line bc-bad" title={r.error}>
                          {truncateError(r.error)}
                        </span>
                      )}
                    </>
                  }
                  detail={<span className="bc-detail-sm">{relativeAgo(r.createdAt)}</span>}
                />
              )}
            />
          )}
        </ul>
      )}
    </section>
  );
}

export default function Admin(): JSX.Element {
  return (
    <Page title="Admin" sub="System health and usage" className="bc-admin">
      <SystemSection />
      <AssistantSection />
      <AiUsageSection />
      <IngestSection />
    </Page>
  );
}
